import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { Status } from '@thuas/pd-schemas/built/generated/api-types';
import { ISiteState } from 'features/site/siteSlice';
import { pickPrimitiveProps } from 'helpers/objects';
import { IMenu, deleteMenu, fetchMenus, patchMenu, postMenu } from './menuAPI';

type Status = (typeof Status)[keyof typeof Status];

export interface IMenusState {
  menus: IMenu[];
  status: 'idle' | 'loading' | 'failed';
  errors: any;
}

export type MenusStateProps = {
  menus: IMenu[];
};

export const initialState: IMenusState = {
  menus: [],
  status: 'idle',
  errors: '',
};

export const fetchMenusAsync = createAsyncThunk('menu/fetchMenus', async () => {
  const res = await fetchMenus();
  return { response: res };
});

export const postMenuAsync = createAsyncThunk(
  'menu/postMenu',
  async (
    {
      data,
    }: {
      data: IMenu;
    },
    thunkAPI,
  ) => {
    try {
      return { response: await postMenu(data) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const patchMenuAsync = createAsyncThunk(
  'menu/patchMenu',
  async (
    {
      data,
    }: {
      data: IMenu;
    },
    thunkAPI,
  ) => {
    try {
      const res = { response: await patchMenu(data) };
      return res;
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const deleteMenuAsync = createAsyncThunk(
  'menu/deleteMenu',
  async (
    {
      id,
    }: {
      id: number;
    },
    thunkAPI,
  ) => {
    try {
      const response = await deleteMenu(id);
      return {
        response,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export function addMenuCases(builder: ActionReducerMapBuilder<ISiteState>) {
  builder
    .addCase(fetchMenusAsync.pending, (state) => {
      state.status = 'loading';
    })
    .addCase(fetchMenusAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      state.menus = action.payload.response.data;
    })
    .addCase(fetchMenusAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    })
    .addCase(postMenuAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(postMenuAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      const id = action.payload.response.data._id;
      state.menus.push({ ...action.meta.arg.data, id } as IMenu);
    })
    .addCase(postMenuAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    })
    .addCase(patchMenuAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(patchMenuAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      const menu = state.menus.find((m) => m.id === action.meta.arg.data.id);
      if (menu) {
        Object.assign(menu, pickPrimitiveProps(action.meta.arg.data));
      }
    })
    .addCase(patchMenuAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    })
    .addCase(deleteMenuAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(deleteMenuAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      state.menus = state.menus.filter((m) => m.id !== action.meta.arg.id);
    })
    .addCase(deleteMenuAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    });
}
